import { useEffect } from 'react'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { TooltipProvider } from '@/components/ui/tooltip'
import { GlobalTooltip } from '@/components/ui/global-tooltip'
import { Toaster } from '@/components/ui/sonner'
import { ErrorBoundary } from '@/components/error-boundary'
import { AuthBootstrap } from '@/features/auth/components/auth-bootstrap'
import { MembershipPurchaseDialog } from '@/features/billing/components/membership-purchase-dialog'
import { CreditRechargeDialog } from '@/features/billing/components/credit-recharge-dialog'
import { LocalAgentBridgeBootstrap } from '@/features/assistant/bridge/local-agent-bridge-bootstrap'
import { WorkspaceGate } from '@/features/workspace-gate'
import { useSettingsStore } from '@/features/settings/stores/settings-store'
import { routeTree } from './routeTree.gen'

const router = createRouter({ routeTree })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

export function App() {
  const colorTheme = useSettingsStore((state) => state.colorTheme)

  // Prevent default browser zoom application-wide
  useEffect(() => {
    const wheelListenerOptions: AddEventListenerOptions = { passive: false, capture: true }
    const keyListenerOptions: AddEventListenerOptions = { capture: true }

    const preventBrowserZoom = (e: WheelEvent) => {
      // Prevent browser zoom when Ctrl/Cmd is held
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault()
      }
    }

    const preventKeyboardZoom = (e: KeyboardEvent) => {
      // Prevent browser zoom shortcuts: Ctrl+=/+/-, Ctrl+0
      // Only preventDefault (blocks browser zoom), event still propagates to react-hotkeys-hook
      if (e.ctrlKey || e.metaKey) {
        if (e.key === '+' || e.key === '=' || e.key === '-' || e.key === '_' || e.key === '0') {
          e.preventDefault()
          // DO NOT call stopPropagation() - we want react-hotkeys-hook to still receive this
        }
      }
    }

    // Add listeners at capture phase to intercept before browser handles them
    document.addEventListener('wheel', preventBrowserZoom, wheelListenerOptions)
    document.addEventListener('keydown', preventKeyboardZoom, keyListenerOptions)

    return () => {
      document.removeEventListener('wheel', preventBrowserZoom, wheelListenerOptions)
      document.removeEventListener('keydown', preventKeyboardZoom, keyListenerOptions)
    }
  }, [])

  useEffect(() => {
    document.documentElement.dataset.colorTheme = colorTheme
  }, [colorTheme])

  // TooltipProvider at app level to prevent re-renders cascading from Editor
  // GlobalTooltip for performant data-tooltip based tooltips
  // Toaster for toast notifications
  // ErrorBoundary for graceful error recovery
  // WorkspaceGate blocks RouterProvider until a workspace handle is granted.
  // Mounted HERE (not inside __root.tsx) so route loaders — which run before
  // children components mount — never see an uninitialized workspace root.
  return (
    <ErrorBoundary level="app">
      <TooltipProvider delayDuration={300}>
        <AuthBootstrap>
          <WorkspaceGate>
            <LocalAgentBridgeBootstrap />
            <RouterProvider router={router} />
          </WorkspaceGate>
        </AuthBootstrap>
        <MembershipPurchaseDialog />
        <CreditRechargeDialog />
        <GlobalTooltip />
        <Toaster />
      </TooltipProvider>
    </ErrorBoundary>
  )
}
